import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { SaasTenantPicker } from './SaasTenantPicker.js';

interface DomainRow {
  readonly domainId: string;
  readonly saasTenantId: string;
  readonly fqdn: string;
  readonly domainType: string;
  readonly isPrimary: boolean;
  readonly status: string;
}

const DOMAIN_TYPES = ['SYSTEM_SUBDOMAIN', 'CUSTOM_DOMAIN'];
const STATUSES = ['PENDING', 'VERIFYING', 'VERIFIED', 'FAILED', 'EXPIRED', 'REVOKED'];
const PAGE = 25;

/**
 * M43 §27 Domain Management Screen (§10 Domain Management). Operators map system
 * subdomains or custom domains to a tenant, trigger DNS verification and revoke
 * domains. Viewing needs DOMAIN_VIEW; add/verify/revoke need DOMAIN_MANAGE.
 */
export function DomainManagementScreen(): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<DomainRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [saasTenantId, setSaasTenantId] = useState('');
  const [fqdn, setFqdn] = useState('');
  const [domainType, setDomainType] = useState('CUSTOM_DOMAIN');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: DomainRow[]; total: number }>(`/v1/saas/domains?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, statusFilter, page, reload]);

  const add = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!saasTenantId.trim()) { setActionError('A tenant id is required.'); return; }
    if (!fqdn.trim()) { setActionError('A domain name is required.'); return; }
    try {
      await apiRequest('/v1/saas/domains', {
        method: 'POST',
        body: { saasTenantId: saasTenantId.trim(), fqdn: fqdn.trim(), domainType },
        token,
      });
      setNotice('Domain added.');
      setFqdn('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setActionError('You are not authorised to add domains (DOMAIN_MANAGE required).');
      else setActionError((err as Error).message);
    }
  };

  const verify = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/saas/domains/${id}/verify`, { method: 'POST', body: { dnsConfirmed: true }, token });
      setNotice('Domain verification requested.');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setActionError('You are not authorised to verify domains (DOMAIN_MANAGE required).');
      else setActionError((err as Error).message);
    }
  };

  const revoke = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/saas/domains/${id}`, { method: 'DELETE', token });
      setNotice('Domain revoked.');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setActionError('You are not authorised to revoke domains (DOMAIN_MANAGE required).');
      else setActionError((err as Error).message);
    }
  };

  return (
    <main aria-label="domain-management-screen">
      <h1>Domain Management</h1>

      <section aria-label="domain-add">
        <h2>Add Domain</h2>
        <form aria-label="domain-form" onSubmit={(e) => void add(e)}>
          <SaasTenantPicker label="Tenant" value={saasTenantId} onPick={setSaasTenantId} />
          <label>Domain<input aria-label="field-fqdn" value={fqdn} onChange={(e) => setFqdn(e.target.value)} /></label>
          <label>Type
            <select aria-label="field-domain-type" value={domainType} onChange={(e) => setDomainType(e.target.value)}>
              {DOMAIN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-domain">Add</button>
        </form>
        {actionError && <p role="alert" aria-label="domain-action-error">{actionError}</p>}
        {notice && <p aria-label="domain-notice">{notice}</p>}
      </section>

      <section aria-label="domain-filters">
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert" aria-label="domain-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="domain-total">{total} domain(s).</p>
          <table aria-label="domain-table">
            <thead><tr><th scope="col">Domain</th><th scope="col">Type</th><th scope="col">Primary</th><th scope="col">Status</th><th scope="col">Actions</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5}>No domains.</td></tr>
              ) : rows.map((d) => (
                <tr key={d.domainId} data-domain={d.domainId}>
                  <td aria-label={`domain-fqdn-${d.domainId}`}>{d.fqdn}</td>
                  <td>{d.domainType}</td>
                  <td>{d.isPrimary ? 'Yes' : 'No'}</td>
                  <td aria-label={`domain-status-${d.domainId}`}>{d.status}</td>
                  <td>
                    <button type="button" aria-label={`verify-${d.domainId}`} onClick={() => void verify(d.domainId)}>Verify</button>
                    <button type="button" aria-label={`revoke-${d.domainId}`} onClick={() => void revoke(d.domainId)}>Revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="domain-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
